import { ColumnConfig, SortingOptionsType, Table, TableBody } from '@components/design/migrated/Table';
import classnames from 'classnames';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import {
  ColumnHeader,
  ColumnHeaderDisplayName,
  defaultItemFeature,
  DefaultSortingOptionsForProducts,
  ItemFeaturesResp,
  LeaderboardKpi,
  ProductAnalyzerGroupKpi,
  SaleType,
  TopSellingProductsTableId,
} from '@models/Products';
import { NoResult } from '@pages/NoResult';
import usePersistedSortingProducts from '@utils/hook/sorting/UsePersistedSortingProducts';
import { GrowthTypes } from '../comparison-growth-selector/Types';
import { FocusArea } from '../products-top-5-table/Types';
import { getKpiWithMarketShare } from '../products-top-5-table/utils/ProductsUtils';
import { getProductsColumns } from './products-table-columns/ProductsTableColumns';
import { DisplayOptions } from './products-table-filter/Types';
import * as styles from './ProductsTable.module.scss';
import { FactRevenue, Filters } from '@utils/pageConfig/Types';
import ProductsTableRow from './products-table-row/ProductsTableRow';
import { ProductsTableGroupRows } from './model-group/ProductsTableGroupRows';
import { GroupContext } from '@utils/userPreferencesUtils/Types';
import { isPeriodYTDOrMAT } from '@utils/PeriodUtils';
import { useSelector } from 'react-redux';
import { IRootState } from '@state/reducers/Reducers';

interface Props {
  productsLeaderboard: LeaderboardKpi[];
  itemFeatures?: ItemFeaturesResp[]; // only applicable to leaderboard tab
  selectedDisplayType: DisplayOptions;
  selectedSaleType: SaleType;
  selectedFocusArea?: FocusArea;
  factRevenue: FactRevenue;
  selectedGrowthType: GrowthTypes;
  setSelectedSaleType: Dispatch<SetStateAction<SaleType>>;
  isViewTypeChanged: boolean;
  tableColumns?: ColumnConfig[];
  enableSort?: boolean;
  isTableHeaderSticky?: boolean;
  enableCheckboxes?: boolean;
  productAnalyzerGroupKpi?: ProductAnalyzerGroupKpi[];
  context?: GroupContext;
  isLeaderBoard?: boolean;
  prodLeaderboardFeatureProductFilters: Filters[];
  productSpecificationFilters: Filters[];
}

const findItemFeature = (itemFeatures: ItemFeaturesResp[] | undefined, kpi: any) => {
  return itemFeatures?.find((item: ItemFeaturesResp) => item.itemId === kpi?.item?.itemId);
};

const columnSetter = ({
  tableColumns,
  setColumns,
  selectedDisplayType,
  selectedSaleType,
  features,
  enableCheckboxes,
  productSpecificationFilters,
  isAbsoluteSortOnly,
}: {
  tableColumns: ColumnConfig[] | undefined;
  setColumns: Dispatch<SetStateAction<ColumnConfig[]>>;
  selectedDisplayType: DisplayOptions;
  selectedSaleType: SaleType;
  features: (string | null)[];
  enableCheckboxes?: boolean;
  productSpecificationFilters?: Filters[];
  isAbsoluteSortOnly: boolean;
}) => {
  if (tableColumns) {
    setColumns(tableColumns);
  } else {
    const cols: ColumnConfig[] = [
      {
        headerKey: ColumnHeader.itemName,
        name: ColumnHeaderDisplayName[ColumnHeader.itemName],
        className: classnames([styles.ProductsTable__TableHeader, 'z-40']),
      },
      ...getProductsColumns(
        selectedDisplayType,
        selectedSaleType,
        features,
        productSpecificationFilters,
        isAbsoluteSortOnly,
      ),
    ];
    setColumns(cols);
  }
  if (enableCheckboxes) {
    setColumns((cols) => [
      {
        headerKey: 'checkbox',
        name: '',
        className: classnames([styles.ProductsTable__TableHeader, 'z-40']),
      },
      ...cols,
    ]);
  }
};

const ProductsTable: React.FC<Props> = ({
  productsLeaderboard,
  itemFeatures,
  selectedDisplayType,
  selectedSaleType,
  selectedFocusArea = FocusArea.market,
  factRevenue,
  selectedGrowthType,
  setSelectedSaleType,
  isViewTypeChanged,
  tableColumns,
  enableSort = true,
  isTableHeaderSticky = true,
  enableCheckboxes = false,
  productAnalyzerGroupKpi,
  context = { client: '', country: '' },
  isLeaderBoard = false,
  prodLeaderboardFeatureProductFilters,
  productSpecificationFilters,
}) => {
  const { period } = useSelector((state: IRootState) => state.mainFiltersProducts);

  const [columns, setColumns] = useState<ColumnConfig[]>([]);

  const renderTableBody = () => {
    //Total row should always render first
    const total = productsLeaderboard.find((product) => product?.item?.itemName === 'TOTAL');
    const productGroups = productsLeaderboard.filter((product) => product?.item?.itemName !== 'TOTAL');
    const totalItemFeature = findItemFeature(itemFeatures, total);
    return (
      <>
        {total && (
          <ProductsTableRow
            selectedDisplayType={selectedDisplayType}
            key={'total-row'}
            rowIndex={0}
            leaderboardKpi={total}
            columns={columns}
            itemFeature={totalItemFeature ?? defaultItemFeature}
            className={classnames([styles.ProductsTable__TableRow__WithHover, styles.ProductsTable__ZPositionBack])}
            currency={factRevenue.currencySymbol}
            isStickyRow={false}
            selectedGrowthType={selectedGrowthType}
            selectedSaleType={selectedSaleType}
            selectedFocusArea={selectedFocusArea}
            {...(enableSort && { sortedColumn: sorting.headerKey })}
          />
        )}
        {isLeaderBoard && (
          <ProductsTableGroupRows
            columns={columns}
            sortedColumn={sorting.headerKey}
            context={context}
            productAnalyzerGroupKpi={productAnalyzerGroupKpi ?? []}
            itemFeatures={itemFeatures}
            selectedDisplayType={selectedDisplayType}
            selectedSaleType={selectedSaleType}
            selectedGrowthType={selectedGrowthType}
            selectedFocusArea={selectedFocusArea}
            currency={factRevenue.currencySymbol}
            enableSort={enableSort}
          />
        )}
        <TableBody>
          {productGroups.map((kpiArg, index) => {
            let kpi = kpiArg;
            const itemFeature = findItemFeature(itemFeatures, kpi);

            if (selectedDisplayType === DisplayOptions.trends) {
              kpi = {
                ...getKpiWithMarketShare(productsLeaderboard, kpi, selectedSaleType, selectedFocusArea),
              };
            }

            return (
              <ProductsTableRow
                selectedDisplayType={selectedDisplayType}
                key={index + 1}
                rowIndex={index + 1}
                leaderboardKpi={kpi}
                columns={columns}
                itemFeature={itemFeature ?? defaultItemFeature}
                className={classnames(
                  [styles.ProductsTable__TableRow__WithHover, styles.ProductsTable__ZPositionBack],
                  { [styles.ProductsTable__TableRow__WithHover__RetailerKpiZero]: isRetailerKpiZero(kpi) },
                )}
                currency={factRevenue.currencySymbol}
                isStickyRow={false}
                selectedGrowthType={selectedGrowthType}
                selectedSaleType={selectedSaleType}
                selectedFocusArea={selectedFocusArea}
                {...(enableSort && { sortedColumn: sorting.headerKey })}
              />
            );
          })}
        </TableBody>
      </>
    );
  };

  const { sorting, updateOptions } = usePersistedSortingProducts({
    tableId: TopSellingProductsTableId,
    defaultSorting: DefaultSortingOptionsForProducts,
    selectedSaleType,
    setSelectedSaleType,
    selectedDisplayType,
    isViewTypeChanged,
  });

  const updateSort = useCallback(
    (options: SortingOptionsType) => {
      updateOptions(options);
    },
    [updateOptions],
  );

  const tableSortParams = {
    sortingOptions: sorting,
    setSortingOptions: updateSort,
    defaultSortingOptions: sorting,
  };

  const isFeatureView = selectedDisplayType === DisplayOptions.feature;

  useEffect(() => {
    if (productsLeaderboard) {
      const features = prodLeaderboardFeatureProductFilters.map((f) => f.feature);

      columnSetter({
        tableColumns,
        setColumns,
        selectedDisplayType,
        selectedSaleType,
        features,
        enableCheckboxes,
        productSpecificationFilters,
        isAbsoluteSortOnly: isPeriodYTDOrMAT(period),
      });
    }
  }, [productsLeaderboard, selectedDisplayType, selectedSaleType, sorting, tableColumns, period.value]);

  return !productsLeaderboard || productsLeaderboard.length === 0 ? (
    <div>{NoResult()}</div>
  ) : (
    <div
      className={classnames({
        [styles['ProductsTable_View']]: isFeatureView,
      })}
      data-testid="Top-selling-product"
    >
      <Table
        dontWrap={true}
        columns={columns}
        isHeaderSticky={isTableHeaderSticky}
        isFirstColumnSticky={isFeatureView}
        {...(enableSort && tableSortParams)}
        className={styles.ProductsTable__Table}
      >
        {renderTableBody()}
      </Table>
    </div>
  );
};
export const isRetailerKpiZero = (productRow: LeaderboardKpi) => {
  return productRow?.retailerRevenue?.value == 0 && productRow?.retailerVolume?.value == 0;
};
export default ProductsTable;
